import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Phone,
  MapPin,
  ArrowUp,
  CalendarDays
} from "lucide-react";
import logoImg from "../assets/jalsa-logo.jpeg";
import {
  foodCategories,
  drinksCategories,
  foodItems,
  drinksItems
} from "../data/menuData";
import { validateMenuData } from "../utils/validateMenuData";

import MenuHero from "../components/menu/MenuHero";
import MenuSwitcher from "../components/menu/MenuSwitcher";
import MenuSearch from "../components/menu/MenuSearch";
import MenuCategorySection from "../components/menu/MenuCategorySection";
import ZeroState from "../components/menu/ZeroState";

import "../styles/MenuPage.css";

function MenuPage({ setReserveOpen }) {
  const navigate = useNavigate();

  // Run automated validation check on mount
  useEffect(() => {
    validateMenuData(foodCategories, drinksCategories, foodItems, drinksItems);
    window.scrollTo(0, 0);
  }, []);

  const [menuType, setMenuType] = useState("food"); // "food" | "drinks"
  const [searchQuery, setSearchQuery] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Track window scroll for Back-To-Top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Get active categories & items
  const activeCategories = menuType === "food" ? foodCategories : drinksCategories;
  const rawItems = menuType === "food" ? foodItems : drinksItems;

  // Filter items based on searchQuery
  const filteredItems = useMemo(() => {
    if (searchQuery.trim() === "") return rawItems;
    const query = searchQuery.toLowerCase();
    return rawItems.filter((item) => {
      const matchName = item.name.toLowerCase().includes(query);
      const matchDesc = item.description ? item.description.toLowerCase().includes(query) : false;
      const matchBadge = item.badge ? item.badge.toLowerCase().includes(query) : false;
      const catObj = activeCategories.find((c) => c.id === item.categoryId);
      const matchCat = catObj ? catObj.label.toLowerCase().includes(query) : false;

      return matchName || matchDesc || matchBadge || matchCat;
    });
  }, [rawItems, searchQuery, activeCategories]);

  // Group filtered items by category so ALL categories display continuously on one page
  const groupedCategories = useMemo(() => {
    const map = {};
    activeCategories.forEach((cat) => {
      map[cat.id] = { category: cat, items: [] };
    });

    filteredItems.forEach((item) => {
      if (map[item.categoryId]) {
        map[item.categoryId].items.push(item);
      }
    });

    return Object.values(map).filter((group) => group.items.length > 0);
  }, [activeCategories, filteredItems]);

  // Switch between Food and Drinks
  const handleMenuTypeChange = (type) => {
    setMenuType(type);
    setSearchQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="menu-page">
      {/* Top Action Navigation Bar */}
      <header className="menu-top-bar">
        <button className="back-home-btn" onClick={() => navigate("/")} aria-label="Back to Home">
          <ArrowLeft size={16} /> <span>Back to Home</span>
        </button>
        <Link to="/" className="menu-brand-link" aria-label="Jalsa Home">
          <img src={logoImg} alt="Jalsa Logo" />
        </Link>
        <button className="gold-btn reserve-top-btn" onClick={() => setReserveOpen(true)}>
          Reserve <ArrowUpRight size={15} />
        </button>
      </header>

      {/* Sticky Control Bar (Master Switcher + Search) */}
      <section className="menu-control-bar">
        <div className="control-bar-inner">
          <MenuSwitcher
            menuType={menuType}
            foodCount={foodItems.length}
            drinksCount={drinksItems.length}
            onSwitch={handleMenuTypeChange}
          />

          <MenuSearch
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            menuType={menuType}
          />
        </div>
      </section>

      {/* Main Magazine Canvas Container */}
      <main className="menu-magazine-canvas">
        <MenuHero />

        {/* Continuous Single-Page Menu Rendering All Categories */}
        <section className="menu-sections-container">
          {groupedCategories.length > 0 ? (
            groupedCategories.map(({ category, items }) => (
              <MenuCategorySection
                key={category.id}
                category={category}
                items={items}
              />
            ))
          ) : (
            <ZeroState
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              menuType={menuType}
              onClear={() => setSearchQuery("")}
            />
          )}
        </section>


      </main>

      {/* Floating Action Controls */}
      <div className="floating-actions-bar">
        <button
          className="gold-btn floating-reserve-pill"
          onClick={() => setReserveOpen(true)}
          aria-label="Reserve a table"
        >
          <CalendarDays size={16} /> <span>Reserve Table</span>
        </button>

        {showBackToTop && (
          <button
            className="floating-back-top-btn"
            onClick={scrollToTop}
            aria-label="Scroll back to top"
          >
            <ArrowUp size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

export default MenuPage;
